/**
 * Essential Cart Drawer — JavaScript
 * Version: 1.0.0
 * Features: AJAX cart, countdown, reward bar, upsells, discount codes, sticky bar
 */

(function () {
  "use strict";

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  function clearThemeLoadingState() {
    if (window.theme && theme.loadingState && typeof theme.loadingState.destroyAll === "function") {
      theme.loadingState.destroyAll();
    }
  }

  function formatMoney(cents, format) {
    const fmt = format || window.CartDrawerSettings?.moneyFormat || "${{amount}}";
    const value = Number(cents || 0) / 100;

    const formatFixed = (num, decimals, thousandsSep, decimalSep) => {
      const fixed = Number(num).toFixed(decimals);
      const parts = fixed.split(".");
      const intPart = parts[0];
      const decPart = parts[1] || "";
      const sign = intPart.startsWith("-") ? "-" : "";
      const digits = sign ? intPart.slice(1) : intPart;
      const withThousands = digits.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      return decimals > 0
        ? `${sign}${withThousands}${decimalSep}${decPart}`
        : `${sign}${withThousands}`;
    };

    const amount = formatFixed(value, 2, ",", ".");
    const amountWithComma = formatFixed(value, 2, ".", ",");
    const amountNoDecimals = formatFixed(value, 0, ",", ".");
    const amountNoDecimalsWithComma = formatFixed(value, 0, ".", ",");
    const amountNoDecimalsWithSpace = formatFixed(value, 0, " ", ",");

    return fmt
      .split("{{amount_no_decimals_with_comma_separator}}").join(amountNoDecimalsWithComma)
      .split("{{amount_no_decimals_with_space_separator}}").join(amountNoDecimalsWithSpace)
      .split("{{amount_with_comma_separator}}").join(amountWithComma)
      .split("{{amount_no_decimals}}").join(amountNoDecimals)
      .split("{{amount}}").join(amount);
  }

  const CartAPI = {
    async getCart() {
      const res = await fetch("/cart.js");
      if (!res.ok) throw new Error("Cart fetch failed");
      return res.json();
    },

    async updateItem(key, quantity) {
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: { [key]: quantity } }),
      });
      if (!res.ok) throw new Error("Cart update failed");
      return res.json();
    },

    async addItem(variantId, quantity = 1, properties = {}) {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity, properties }] }),
      });
      if (!res.ok) throw new Error("Add to cart failed");
      return res.json();
    },

    async updateAttributes(attributes) {
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes }),
      });
      if (!res.ok) throw new Error("Cart attributes update failed");
      return res.json();
    },

    async applyDiscount(code) {
      const cleanCode = (code || "").trim();
      if (!cleanCode) throw new Error("Empty discount code");

      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discount: cleanCode
        }),
      });
      if (!res.ok) throw new Error("Discount update failed");
      const cart = await res.json();

      const hasCartLevelDiscount = Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0;
      const hasDiscountApplications = Array.isArray(cart.discount_applications) && cart.discount_applications.length > 0;
      const hasLineLevelDiscounts = Array.isArray(cart.items) && cart.items.some((item) => {
        return Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0;
      });

      return {
        cart,
        applied: hasCartLevelDiscount || hasDiscountApplications || hasLineLevelDiscounts
      };
    },

    async clearDiscount() {
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discount: ""
        })
      });
      if (!res.ok) throw new Error("Discount clear failed");
      return res.json();
    },

    async updateNote(note) {
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      });
      if (!res.ok) throw new Error("Note update failed");
      return res.json();
    },
  };

  class CountdownTimer {
    constructor(settings) {
      this.settings = settings;
      this.els = {
        drawerTime: $("#cart-countdown-time"),
        stickyTime: $("#sticky-countdown-time"),
      };
      this.totalSeconds = (settings.countdownMinutes || 10) * 60;
      this.remaining = this._loadOrInit();
      this.interval = null;
    }

    _storageKey() { return "cd_timer_" + (this.settings.countdownMinutes || 10); }

    _loadOrInit() {
      const saved = sessionStorage.getItem(this._storageKey());
      if (saved) {
        const { remaining, ts } = JSON.parse(saved);
        const elapsed = Math.floor((Date.now() - ts) / 1000);
        const left = remaining - elapsed;
        if (left > 0) return left;
      }
      this._save(this.totalSeconds);
      return this.totalSeconds;
    }

    _save(remaining) {
      sessionStorage.setItem(this._storageKey(), JSON.stringify({ remaining, ts: Date.now() }));
    }

    _format(secs) {
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return `${m}:${s.toString().padStart(2, "0")}`;
    }

    _update() {
      const t = this._format(this.remaining);
      if (this.els.drawerTime) {
        this.els.drawerTime.textContent = t;
        this.els.drawerTime.classList.toggle("is-urgent", this.remaining <= 60);
      }
      if (this.els.stickyTime) this.els.stickyTime.textContent = t;
    }

    reset() {
      this.remaining = this.totalSeconds;
      this._save(this.remaining);
      this._update();
    }

    start() {
      this._update();
      this.interval = setInterval(() => {
        if (this.remaining > 0) {
          this.remaining--;
          this._save(this.remaining);
          this._update();
        } else {
          clearInterval(this.interval);
        }
      }, 1000);
    }
  }

  class RewardBar {
    constructor(el) {
      this.el = el;
      this.tiers = [];
      try {
        this.tiers = JSON.parse(el.dataset.tiers || "[]");
      } catch (_e) {
        this.tiers = [];
      }
      this.barFill = $("#reward-bar-fill", el);
      this.messageEl = $("#reward-bar-message", el);
    }

    update(cartTotalCents) {
      if (!this.tiers.length) return;
      const activeTiers = this.tiers.filter((t) => t.amount > 0).sort((a, b) => a.amount - b.amount);
      if (!activeTiers.length) return;

      const maxAmount = activeTiers[activeTiers.length - 1].amount;
      const pct = Math.min((cartTotalCents / maxAmount) * 100, 100);

      if (this.barFill) {
        this.barFill.style.width = pct + "%";
        this.barFill.setAttribute("aria-valuenow", Math.round(pct));
      }

      let msg = "";
      let reached = false;
      for (let i = 0; i < activeTiers.length; i++) {
        const tier = activeTiers[i];
        if (cartTotalCents >= tier.amount) {
          const nextTier = activeTiers[i + 1];
          if (!nextTier) {
            msg = tier.reachedMsg;
            reached = true;
          }
        } else {
          const remaining = formatMoney(tier.amount - cartTotalCents);
          msg = tier.progressMsg.replace("{amount}", remaining);
          break;
        }
      }

      if (this.messageEl) {
        this.messageEl.innerHTML = msg;
        this.messageEl.classList.toggle("is-reached", reached);
      }
    }
  }

  class UpsellCarousel {
    constructor(el, settings) {
      this.el = el;
      this.settings = settings;
      this.track = $("#upsells-track", el);
      this.prevBtn = $("#upsells-prev", el);
      this.nextBtn = $("#upsells-next", el);
      this.useRecommendations = el.dataset.useRecommendations === "true";
      this.currentIndex = 0;

      if (this.useRecommendations) {
        this._loadRecommendations();
      } else {
        this._init();
      }
    }

    async _loadRecommendations(passedCart) {
      const cartRes = passedCart || await CartAPI.getCart();
      if (!cartRes.items?.length) {
        await this._loadCatalogFallback();
        return;
      }

      const productId = cartRes.items[0].product_id;
      const limit = this.settings.upsellMaxItems || 3;

      try {
        const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`);
        const data = await res.json();

        if (!data.products?.length) {
          this.el.style.display = "none";
          return;
        }

        const cartIds = cartRes.items.map((i) => i.product_id);
        this.track.innerHTML = "";

        data.products.forEach((product) => {
          if (this.settings.upsellHideIfInCart && cartIds.includes(product.id)) return;
          const variant = product.variants[0];
          const hasOptions = product.variants.length > 1;
          const item = document.createElement("div");
          item.className = "cart-upsell__item";
          item.dataset.productId = product.id;
          const imgSrc = product.featured_image ? product.featured_image + "&width=80" : "";

          item.innerHTML = `
            ${imgSrc ? `<img class="cart-upsell__image" src="${imgSrc}" alt="${product.title}" width="48" height="48" loading="lazy">` : '<div class="cart-upsell__image-placeholder"></div>'}
            <div class="cart-upsell__info">
              <p class="cart-upsell__name">${product.title.length > 30 ? `${product.title.slice(0, 30)}...` : product.title}</p>
              <div class="cart-upsell__price">
                ${variant.compare_at_price > variant.price ? `<s class="data-wpd-hide cart-upsell__price-compare">${formatMoney(variant.compare_at_price)}</s>` : ""}
                <span class="cart-upsell__price-current" data-wpd-product-handle="${product.handle}" data-wpd-variant-id="${variant.id}" data-wpd-variant-price="${variant.price}" data-wpd-variant-compare-at-price="${variant.compare_at_price}" data-wpd-product-id="${product.id}">${formatMoney(variant.price)}</span>
              </div>
            </div>
            ${hasOptions ? `<a href="/products/${product.handle}" class="cart-upsell__add-btn cart-upsell__add-btn--link">Zobacz</a>` : `<button type="button" class="cart-upsell__add-btn" data-variant-id="${variant.id}" data-product-title="${product.title}">Dodaj</button>`}
          `;
          this.track.appendChild(item);
        });

        if (!this.track.children.length) {
          this.el.style.display = "none";
          return;
        }

        this.el.style.display = "";
        this._init();
      } catch (_e) {
        await this._loadCatalogFallback();
      }
    }

    async _loadCatalogFallback() {
      const limit = this.settings.upsellMaxItems || 3;

      try {
        const res = await fetch(`/products.json?limit=${limit}`);
        if (!res.ok) {
          throw new Error("Catalog fallback failed");
        }

        const data = await res.json();
        const products = data && Array.isArray(data.products) ? data.products : [];

        if (!products.length) {
          this.track.innerHTML = '<div class="cart-upsells__empty">Brak rekomendacji w tej chwili.</div>';
          this.el.style.display = "";
          return;
        }

        this.track.innerHTML = "";

        products.slice(0, limit).forEach((product) => {
          const variant = product.variants && product.variants.length ? product.variants[0] : null;
          if (!variant) return;

          const hasOptions = Array.isArray(product.options) && product.options.length > 1;
          const imageSrc = product.image && product.image.src ? `${product.image.src}&width=80` : "";
          const compareAt = variant.compare_at_price ? Number(variant.compare_at_price) : 0;
          const price = Number(variant.price || 0) * 100;
          const compareAtCents = compareAt > 0 ? compareAt * 100 : 0;

          const item = document.createElement("div");
          item.className = "cart-upsell__item";
          item.dataset.productId = product.id;
          item.innerHTML = `
            ${imageSrc ? `<img class="cart-upsell__image" src="${imageSrc}" alt="${product.title}" width="48" height="48" loading="lazy">` : '<div class="cart-upsell__image-placeholder"></div>'}
            <div class="cart-upsell__info">
              <p class="cart-upsell__name">${product.title.length > 30 ? `${product.title.slice(0, 30)}...` : product.title}</p>
              <div class="cart-upsell__price">
                ${compareAtCents > price ? `<s class="cart-upsell__price-compare">${formatMoney(compareAtCents)}</s>` : ""}
                <span class="cart-upsell__price-current">${formatMoney(price)}</span>
              </div>
            </div>
            ${hasOptions ? `<a href="/products/${product.handle}" class="cart-upsell__add-btn cart-upsell__add-btn--link">Zobacz</a>` : `<button type="button" class="cart-upsell__add-btn" data-variant-id="${variant.id}" data-product-title="${product.title}">Dodaj</button>`}
          `;
          this.track.appendChild(item);
        });

        if (!this.track.children.length) {
          this.track.innerHTML = '<div class="cart-upsells__empty">Brak rekomendacji w tej chwili.</div>';
          this.el.style.display = "";
          return;
        }

        this.el.style.display = "";
        this._init();
      } catch (_e) {
        this.track.innerHTML = '<div class="cart-upsells__empty">Brak rekomendacji w tej chwili.</div>';
        this.el.style.display = "";
      }
    }

    _init() {
      const items = $$(".cart-upsell__item", this.track);
      if (!items.length) {
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        return;
      }

      let current = Math.max(0, Math.min(this.currentIndex, items.length - 1));
      const total = items.length;
      const scroll = (idx) => {
        current = Math.max(0, Math.min(idx, total - 1));
        this.currentIndex = current;
        items[current]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
        if (this.prevBtn) this.prevBtn.disabled = current === 0;
        if (this.nextBtn) this.nextBtn.disabled = current >= total - 1;
      };

      if (this.prevBtn) {
        this.prevBtn.onclick = () => scroll(current - 1);
      }

      if (this.nextBtn) {
        this.nextBtn.onclick = () => scroll(current + 1);
      }

      scroll(0);

      this.el.onclick = async (e) => {
        const btn = e.target.closest(".cart-upsell__add-btn[data-variant-id]");
        if (!btn) return;
        btn.disabled = true;
        btn.textContent = "...";
        try {
          const upsellMarker = "cart_upsell_steroid";
          await CartAPI.addItem(parseInt(btn.dataset.variantId, 10), 1, {
            _upsell_source: upsellMarker
          });
          await CartAPI.updateAttributes({
            cart_upsell_steroid: "yes"
          });
          if (CartDrawerController.instance) {
            CartDrawerController.instance.refresh();
          }
          btn.closest(".cart-upsell__item").style.opacity = "0.4";
          btn.textContent = "Dodano";
        } catch (_err) {
          btn.textContent = "Error";
          btn.disabled = false;
          setTimeout(() => { btn.textContent = "Dodaj"; btn.disabled = false; }, 1500);
        }
      };
    }

    async refresh(cart) {
      if (this.useRecommendations) {
        await this._loadRecommendations(cart);
        return;
      }

      const items = $$(".cart-upsell__item", this.track);
      if (!items.length) {
        this._init();
        return;
      }

      const cartIds = new Set((cart.items || []).map((item) => item.product_id));
      let visibleCount = 0;

      items.forEach((item) => {
        const productId = Number(item.dataset.productId);
        const shouldHide = this.settings.upsellHideIfInCart && cartIds.has(productId);
        item.style.display = shouldHide ? "none" : "";
        if (!shouldHide) visibleCount += 1;
      });

      if (!visibleCount) {
        this.track.innerHTML = '<div class="cart-upsells__empty">Brak rekomendacji w tej chwili.</div>';
      }

      this._init();
    }
  }

  class DiscountManager {
    constructor(settings) {
      this.settings = settings;
      this.toggleBtn = $("#cart-discount-toggle");
      this.toggleLabel = $("#cart-discount-toggle-label");
      this.formWrapper = $("#cart-discount-form-wrapper");
      this.input = $("#cart-discount-input");
      this.applyBtn = $("#cart-discount-apply");
      this.tagsEl = $("#cart-discount-tags");
      this.errorEl = $("#cart-discount-error");
      this.codes = this._loadCodes();
      this._render();
      this._bind();
      this._updateApplyState();
    }

    _storageKey() { return "cd_discounts"; }
    _loadCodes() {
      try { return JSON.parse(localStorage.getItem(this._storageKey()) || "[]"); }
      catch { return []; }
    }
    _saveCodes() { localStorage.setItem(this._storageKey(), JSON.stringify(this.codes)); }

    _render() {
      if (!this.tagsEl) return;
      this.tagsEl.innerHTML = this.codes.map((c) => `
        <div class="cart-discount__tag">
          <span>${c}</span>
          <button type="button" class="cart-discount__tag-remove" data-code="${c}" aria-label="Remove ${c}">x</button>
        </div>
      `).join("");
      if (this.toggleLabel) {
        this.toggleLabel.textContent = this.codes.length ? "Kod zastosowany" : "Masz kod rabatowy?";
      }
      this._syncCheckoutDiscountField();
    }

    _bind() {
      on(this.toggleBtn, "click", (event) => {
        if (event.target && event.target.closest(".cart-discount__tag-remove")) {
          return;
        }
        const isExpanded = this.toggleBtn.getAttribute("aria-expanded") === "true";
        this._setExpanded(!isExpanded);
      });

      on(this.applyBtn, "click", () => this._apply());
      on(this.input, "input", () => this._updateApplyState());
      on(this.input, "keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (!this.applyBtn.disabled) {
            this._apply();
          }
        }
      });
      on(this.tagsEl, "click", async (e) => {
        const btn = e.target.closest(".cart-discount__tag-remove");
        if (!btn) return;
        await this._remove(btn.dataset.code);
      });
    }

    _setExpanded(expanded) {
      if (!this.toggleBtn || !this.formWrapper) return;
      this.toggleBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
      if (expanded) {
        this.formWrapper.removeAttribute("hidden");
        this.input?.focus();
      } else {
        this.formWrapper.setAttribute("hidden", "hidden");
      }
    }

    _updateApplyState() {
      if (!this.applyBtn || !this.input) return;
      const hasValue = !!this.input.value.trim();
      this.applyBtn.disabled = !hasValue;
    }

    getPrimaryCode() {
      return this.codes.length ? this.codes[0] : "";
    }

    _syncCheckoutDiscountField() {
      const form = document.getElementById("cart-form");
      if (!form) return;

      let field = form.querySelector("input[name='discount']");
      const code = this.getPrimaryCode();

      if (!field && code) {
        field = document.createElement("input");
        field.type = "hidden";
        field.name = "discount";
        form.prepend(field);
      }

      if (field) {
        field.value = code || "";
      }
    }

    async _apply() {
      const code = this.input?.value.trim().toUpperCase();
      if (!code) return;

      this.applyBtn.disabled = true;
      this.applyBtn.textContent = "...";
      try {
        const result = await CartAPI.applyDiscount(code);
        if (!result.applied) {
          await CartAPI.clearDiscount();
          this.codes = [];
          this._saveCodes();
          this._render();
          if (this.input) this.input.value = "";
          this._showError("Kod rabatowy jest nieprawidlowy.");

          if (CartDrawerController.instance) {
            await CartDrawerController.instance.refresh();
          }
          return;
        }

        this.codes = [code];
        this._saveCodes();
        this._render();
        if (this.input) this.input.value = "";
        this._updateApplyState();
        this._setExpanded(false);
        this._clearError();

        if (CartDrawerController.instance) {
          await CartDrawerController.instance.refresh();
        }
      } catch (_e) {
        this._showError("Nie udalo sie zastosowac kodu. Sprobuj ponownie.");
      } finally {
        this.applyBtn.disabled = false;
        this.applyBtn.textContent = this.settings.discountBtnLabel || "Apply";
      }
    }

    async _remove(code) {
      this.codes = this.codes.filter((c) => c !== code);
      this._saveCodes();
      this._render();
      if (!this.codes.length) {
        try {
          await CartAPI.clearDiscount();
          if (CartDrawerController.instance) {
            await CartDrawerController.instance.refresh();
          }
          this._clearError();
        } catch (_e) {
          this._showError("Nie udalo sie usunac kodu rabatowego.");
        }
      }
    }

    _showError(msg) {
      this._setExpanded(true);
      if (this.errorEl) { this.errorEl.textContent = msg; this.errorEl.hidden = false; }
    }

    _clearError() {
      if (this.errorEl) { this.errorEl.textContent = ""; this.errorEl.hidden = true; }
    }
  }

  class CartDrawerController {
    constructor() {
      this.drawer = $("#cart-drawer");
      this.overlay = $("#cart-drawer-overlay");
      this.closeBtn = $("#cart-drawer-close");
      this.itemsContainer = $("#cart-drawer-items");
      this.itemCountEls = $$("[id='cart-item-count'], .cart-item-count, .cart-count");
      this.productsRowEl = $("#cart-products-row");
      this.discountRowEl = $("#cart-discount-row");
      this.productsTotalEl = $("#cart-products-total");
      this.discountTotalEl = $("#cart-discount-total");
      this.subtotalEl = $("#cart-subtotal-price");
      this.checkoutBtnPrice = $("#checkout-btn-price");
      this.checkoutBtn = $("#cart-checkout-btn");
      this.settings = window.CartDrawerSettings || {};
      this.rewardBar = null;
      this.countdown = null;
      this.discountMgr = null;
      this.upsellCarousel = null;
      this.isOpen = false;

      if (!this.drawer) return;

      this._applySettings();
      this._initFeatures();
      this._bindEvents();
      CartDrawerController.instance = this;
    }

    _applySettings() {
      if (this.settings.drawerPosition) this.drawer.dataset.position = this.settings.drawerPosition;
      if (this.settings.drawerWidth) this.drawer.style.setProperty("--cd-width", this.settings.drawerWidth + "px");
      if (this.settings.checkoutBtnColor) this.drawer.style.setProperty("--checkout-btn-color", this.settings.checkoutBtnColor);
      if (this.settings.checkoutBtnTextColor) this.drawer.style.setProperty("--checkout-btn-text", this.settings.checkoutBtnTextColor);
    }

    _initFeatures() {
      if (this.settings.showCountdown) {
        this.countdown = new CountdownTimer(this.settings);
        this.countdown.start();
      }

      const rewardBarEl = $("#cart-reward-bar");
      if (rewardBarEl && this.settings.showRewardBar) this.rewardBar = new RewardBar(rewardBarEl);

      const upsellsEl = $("#cart-upsells");
      if (upsellsEl && this.settings.showUpsells) {
        this.upsellCarousel = new UpsellCarousel(upsellsEl, this.settings);
      }

      if (this.settings.showDiscountCode) this.discountMgr = new DiscountManager(this.settings);

      const noteInput = $("#cart-note");
      if (noteInput) {
        let noteTimer;
        on(noteInput, "input", () => {
          clearTimeout(noteTimer);
          noteTimer = setTimeout(() => CartAPI.updateNote(noteInput.value), 600);
        });
      }
    }

    _bindEvents() {
      on(document, "click", (e) => {
        const trigger = e.target.closest("[data-cart-trigger], [href='/cart'], a.cart-icon, .cart-link, .js-cart-trigger");
        if (!trigger) return;
        e.preventDefault();
        this.open();
      });

      on(this.closeBtn, "click", () => this.close());
      on(this.overlay, "click", () => this.close());
      on(document, "keydown", (e) => { if (e.key === "Escape" && this.isOpen) this.close(); });

      on(document, "click", async (e) => {
        const btn = e.target.closest(".cart-item__qty-btn");
        if (!btn || btn.getAttribute("aria-disabled") === "true") return;
        const key = btn.dataset.key;
        const qty = parseInt(btn.dataset.qty, 10);
        if (!key) return;
        await this._updateQty(key, qty);
      });

      on(document, "click", async (e) => {
        const btn = e.target.closest(".cart-item__remove");
        if (!btn) return;
        const key = btn.dataset.key;
        if (!key) return;
        const item = btn.closest(".cart-item");
        if (item) item.classList.add("is-removing");
        await this._updateQty(key, 0);
      });

      if (window.jQuery) {
        window.jQuery(document).ajaxSuccess((_event, _xhr, ajaxOptions) => {
          if (!ajaxOptions || !ajaxOptions.url) return;

          if (/\/cart\/add\.js/.test(ajaxOptions.url)) {
            clearThemeLoadingState();
            this.open();
            this.refresh();
            return;
          }

          if (/\/cart\/(change|update)\.js/.test(ajaxOptions.url)) {
            this.refresh();
          }
        });
      }

      on(this.checkoutBtn, "click", (e) => {
        e.preventDefault();
        const localeNoDiscount = window.Shopify && Shopify.locale ? `?locale=${encodeURIComponent(Shopify.locale)}` : "";
        window.location.assign(`/checkout${localeNoDiscount}`);
      });
    }

    async _updateQty(key, qty) {
      this.drawer.classList.add("is-loading");
      try {
        const cart = await CartAPI.updateItem(key, qty);
        this._renderCart(cart);
      } finally {
        this.drawer.classList.remove("is-loading");
      }
    }

    async refresh() {
      try {
        const cart = await CartAPI.getCart();
        this._renderCart(cart);
        clearThemeLoadingState();
      } catch (_e) {}
    }

    _renderCart(cart) {
      this.itemCountEls.forEach((el) => {
        if (!el) return;
        el.textContent = cart.item_count > 0 ? `(${cart.item_count})` : "";
      });

      const productsTotal = typeof cart.original_total_price === "number"
        ? cart.original_total_price
        : (typeof cart.items_subtotal_price === "number" ? cart.items_subtotal_price : cart.total_price);
      const discountValue = Math.max(0, productsTotal - cart.total_price);
      const discountPercent = productsTotal > 0 ? Math.round((discountValue * 100) / productsTotal) : 0;
      const totalMoney = formatMoney(cart.total_price);

      if (this.productsRowEl) {
        if (discountValue > 0) {
          this.productsRowEl.removeAttribute("hidden");
        } else {
          this.productsRowEl.setAttribute("hidden", "hidden");
        }
      }

      if (this.discountRowEl) {
        if (discountValue > 0) {
          this.discountRowEl.removeAttribute("hidden");
        } else {
          this.discountRowEl.setAttribute("hidden", "hidden");
        }
      }

      if (this.productsTotalEl) this.productsTotalEl.textContent = formatMoney(productsTotal);
      if (this.discountTotalEl) {
        this.discountTotalEl.textContent = discountValue > 0
          ? `-${formatMoney(discountValue)} (${discountPercent}%)`
          : formatMoney(0);
      }
      if (this.subtotalEl) this.subtotalEl.textContent = totalMoney;
      if (this.checkoutBtnPrice) this.checkoutBtnPrice.textContent = totalMoney;
      if (this.rewardBar) this.rewardBar.update(cart.total_price);

      if (this.itemsContainer) {
        this._renderItems(cart);
      }

      if (this.upsellCarousel && typeof this.upsellCarousel.refresh === "function") {
        this.upsellCarousel.refresh(cart).catch(() => {});
      }

      document.dispatchEvent(new CustomEvent("cart-updated", { detail: cart }));
    }

    _renderItems(cart) {
      if (cart.item_count === 0) {
        this.itemsContainer.innerHTML = `
          <form action="/cart" method="post" id="cart-form" novalidate>
            <div class="cart-drawer__empty">
              <p class="cart-empty__title">Twoj koszyk jest pusty</p>
              <a href="/collections/all" class="cart-empty__link">Kontynuuj zakupy</a>
            </div>
          </form>`;
        return;
      }

      const itemsMarkup = cart.items.map((item) => {
        const hasDiscount = item.original_price !== item.final_price;
        const variantTitle = item && typeof item.variant_title === "string" ? item.variant_title.trim() : "";
        const hasVariantTitle = !!variantTitle &&
          variantTitle !== "Default Title" &&
          variantTitle.toLowerCase() !== "null" &&
          variantTitle.toLowerCase() !== "undefined";
        const imgHtml = item.image
          ? `<img class="cart-item__image" src="${item.image}?width=120" alt="${item.title}" width="64" height="64" loading="lazy">`
          : `<div class="cart-item__image-placeholder"></div>`;

        return `
          <div class="cart-item" data-key="${item.key}" data-variant-id="${item.variant_id}">
            <a href="${item.url}" class="cart-item__image-link" tabindex="-1" aria-hidden="true">${imgHtml}</a>
            <div class="cart-item__details">
              <a href="${item.url}" class="cart-item__name">${item.product_title}</a>
              ${hasVariantTitle ? `<p class="cart-item__variant">${variantTitle}</p>` : ""}
              <div class="cart-item__bottom">
                <div class="cart-item__qty" role="group">
                  <button type="button" class="cart-item__qty-btn cart-item__qty-btn--minus"
                    data-key="${item.key}" data-qty="${item.quantity - 1}"
                    ${item.quantity <= 1 ? "aria-disabled='true'" : ""}>-</button>
                  <span class="cart-item__qty-num">${item.quantity}</span>
                  <button type="button" class="cart-item__qty-btn cart-item__qty-btn--plus"
                    data-key="${item.key}" data-qty="${item.quantity + 1}">+</button>
                </div>
                <button type="button" class="cart-item__remove" data-key="${item.key}">x</button>
              </div>
            </div>
            <div class="cart-item__price">
              ${hasDiscount ? `<s class="data-wpd-hide cart-item__price-original">${formatMoney(item.original_price * item.quantity)}</s>` : ""}
              <input type="hidden" data-wpd-cart-drawer-product-handle="${item.handle}" data-wpd-cart-drawer-variant-id="${item.id}" data-wpd-cart-drawer-variant-price="${item.price}" data-wpd-cart-drawer-variant-compare-at-price="${item.original_price}" data-wpd-cart-drawer-product-id="${item.product_id}" data-wpd-cart-drawer-line-price data-wpd-cart-drawer-item="${item.key}" data-wpd-cart-drawer-quantity="${item.quantity}" />
              <span class="cart-item__price-final" data-wpd-cart-drawer-product-handle="${item.handle}" data-wpd-cart-drawer-variant-id="${item.id}" data-wpd-cart-drawer-variant-price="${item.price}" data-wpd-cart-drawer-variant-compare-at-price="${item.original_price}" data-wpd-cart-drawer-product-id="${item.product_id}" data-wpd-cart-drawer-line-price data-wpd-cart-drawer-item="${item.key}" data-wpd-cart-drawer-quantity="${item.quantity}">${formatMoney(item.final_price * item.quantity)}</span>
            </div>
          </div>`;
      }).join("");

      this.itemsContainer.innerHTML = `<form action="/cart" method="post" id="cart-form" novalidate><div class="cart-items__list" id="cart-items-list">${itemsMarkup}</div></form>`;
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.drawer.removeAttribute("hidden");
      this.overlay.removeAttribute("hidden");
      this.drawer.classList.add("is-open");
      this.drawer.setAttribute("aria-hidden", "false");
      this.overlay.classList.add("is-active");
      document.body.style.overflow = "hidden";
      this.closeBtn?.focus();
      this.refresh();
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.drawer.classList.remove("is-open");
      this.drawer.setAttribute("aria-hidden", "true");
      this.overlay.classList.remove("is-active");
      document.body.style.overflow = "";
      setTimeout(() => {
        this.drawer.setAttribute("hidden", "hidden");
        this.overlay.setAttribute("hidden", "hidden");
      }, 300);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => new CartDrawerController());
  } else {
    new CartDrawerController();
  }

  window.CartDrawer = CartDrawerController;

  // Hard fallback for header cart icon links:
  // always open drawer instead of navigating to /cart.
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("a.cart-link, a.js-drawer-open-button-right, a[href='/cart'], a[href='/cart/'], a[href^='/cart?'], a[href^='/cart#']");
    if (!trigger) return;

    if (!window.CartDrawer || !window.CartDrawer.instance) return;

    event.preventDefault();
    window.CartDrawer.instance.open();
  }, true);
})();
